import type { AIToolsConfigService } from '@affine/core/modules/ai-button';
import type {
  AIModelService,
  ReasoningModeId,
} from '@affine/core/modules/ai-button/services/models';
import {
  confirmationCompletedEventBus,
  type ConfirmationCompletedEventDetail,
} from '@affine/core/modules/cloud/services/eventsource';
import type { PeekViewService } from '@affine/core/modules/peek-view';
import type { AppThemeService } from '@affine/core/modules/theme';
import type { CopilotChatHistoryFragment } from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import {
  DocModeProvider,
  type FeatureFlagService,
  type NotificationService,
} from '@blocksuite/affine/shared/services';
import { type EditorHost, ShadowlessElement } from '@blocksuite/affine/std';
import type { BaseSelection, ExtensionType } from '@blocksuite/affine/store';
import { ArrowDownBigIcon as ArrowDownIcon } from '@blocksuite/icons/lit';
import type { Signal } from '@preact/signals-core';
import { css, html, nothing, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { debounce } from 'lodash-es';

import type { WorkspaceDocSyncEngine } from '../../../../modules/streamlain/stream-objects';
import { type AIError, AIProvider, UnauthorizedError } from '../../provider';
import {
  aiChatIdentityTypography,
  aiChatStatusTypography,
} from '../../styles/ai-chat-typography';
import { getAIChatOnlineStatus } from '../../utils/chat-connectivity';
import { fetchChatKeysCheck } from '../../utils/chat-keys-check';
import {
  isReasoningModeActive,
  resolveReasoningMode,
  resolveReasoningModel,
} from '../../utils/reasoning-mode';
import { mergeStreamObjects } from '../../utils/stream-objects';
import type { DocDisplayConfig } from '../ai-chat-chips';
import type {
  CanvasAIChatContextMode,
  ChatContextValue,
} from '../ai-chat-content/type';
import {
  AIChatEmptyStateStyles,
  renderAIChatEmptyStateContent,
} from '../ai-chat-empty-state';
import type {
  AINetworkSearchConfig,
  AIReasoningConfig,
} from '../ai-chat-input';
import { resolveCopilotRetrievalPlan } from '../ai-chat-input/retrieval-mode';
import { renderAssistantIdentity } from '../ai-message-content/assistant-identity';
import type { GoalPlanHandle } from '../goal-tree/goal-tree-context';
import { goalActivityText } from '../goal-tree/goal-tree-view-model';
import { shouldHideChatMessage } from './message-visibility';
import { syncMissingMessageIds } from './sync-message-ids';
import {
  type ChatMessage,
  type HistoryMessage,
  isChatAction,
  isChatMessage,
  type StreamObject,
  StreamObjectSchema,
} from './type';

const INITIAL_VISIBLE_MESSAGES = 48;
const PREPEND_VISIBLE_MESSAGES = 24;
const LOAD_MORE_SCROLL_THRESHOLD = 96;

export class AIChatMessages extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    ${AIChatEmptyStateStyles}

    ai-chat-messages {
      position: relative;
    }

    .chat-panel-messages-container {
      --chat-panel-stream-bottom-gutter: 32px;

      display: flex;
      flex-direction: column;
      gap: 24px;
      min-height: 100%;
      position: relative;
      box-sizing: border-box;
      padding-bottom: var(--chat-panel-stream-bottom-gutter);
    }

    chat-panel-assistant-message,
    chat-panel-user-message {
      display: contents;
    }

    .user-info {
      ${aiChatIdentityTypography}
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 4px;
      color: var(--streamlain-text-primary-color);
      user-select: none;
    }

    .messages-placeholder {
      width: 100%;
      position: absolute;
      z-index: 1;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    }
    .independent-mode .messages-placeholder {
      position: static;
      transform: none;
    }

    .down-indicator {
      position: fixed;
      left: 50%;
      transform: translate(-50%, 0);
      bottom: 166px;
      z-index: 1;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      border: 0.5px solid var(--streamlain-border-color);
      background-color: var(--streamlain-background-primary-color);
      box-shadow: var(--streamlain-shadow-2);
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
    }

    .chat-history-window-sentinel {
      height: 1px;
      flex: 0 0 1px;
      pointer-events: none;
    }

    .goal-activity {
      display: grid;
      gap: 0;
      color: var(--streamlain-text-primary-color);
      font-family: var(--streamlain-font-family, inherit);
      animation: goal-activity-reveal 180ms ease-out both;
    }

    .goal-activity-status {
      ${aiChatStatusTypography}
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 34px;
      padding: 8px 10px;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      background: var(--streamlain-background-secondary-color);
      color: var(--streamlain-text-secondary-color);
    }

    @keyframes goal-activity-reveal {
      from {
        opacity: 0;
        transform: translateY(4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .goal-activity {
        animation: none;
      }
    }

    .goal-activity-dots {
      display: inline-flex;
      gap: 3px;
      flex: 0 0 auto;
    }

    .goal-activity-dots i {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: currentColor;
      animation: goal-activity-dot 1.2s ease-in-out infinite;
    }

    .goal-activity-dots i:nth-child(2) {
      animation-delay: 0.15s;
    }

    .goal-activity-dots i:nth-child(3) {
      animation-delay: 0.3s;
    }

    @keyframes goal-activity-dot {
      0%,
      60%,
      100% {
        opacity: 0.35;
        transform: translateY(0);
      }
      30% {
        opacity: 1;
        transform: translateY(-2px);
      }
    }
  `;

  @state()
  accessor _selectionValue: BaseSelection[] = [];

  @state()
  accessor canScrollDown = false;

  /**
   * Tracks if user manually scrolled up during a response.
   * Reset when a new response starts.
   */
  @state()
  accessor userScrolledUp = false;

  @state()
  accessor avatarUrl = '';

  /**
   * Whether AI chat keys are configured (user, workspace, or server)
   * null = checking, true = has keys, false = no keys
   */
  @state()
  accessor hasKeys: boolean | null = null;

  @property({ attribute: false })
  accessor onOpenSettings: (() => void) | undefined;

  @property({ attribute: false })
  accessor noKeysTitle = '';

  @property({ attribute: false })
  accessor noKeysDescription = '';

  @property({ attribute: false })
  accessor noKeysButtonText = '';

  @property({ attribute: false })
  accessor aiStatusReason: string | null = null;

  @property({ attribute: false })
  accessor aiStatusIsOwner = false;

  @property({ attribute: false })
  accessor independentMode: boolean | undefined;

  @property({ attribute: false })
  accessor canvasAIChatContextMode: CanvasAIChatContextMode = 'lineage-only';

  @property({ attribute: false })
  accessor messages!: HistoryMessage[];

  @property({ attribute: false })
  accessor goalPlan: GoalPlanHandle | null = null;

  @property({ attribute: false })
  accessor host: EditorHost | null | undefined;

  @property({ attribute: false })
  accessor workspaceId!: string;

  @property({ attribute: false })
  accessor docId: string | undefined;

  @property({ attribute: false })
  accessor isHistoryLoading!: boolean;

  @property({ attribute: false })
  accessor chatContextValue!: ChatContextValue;

  @property({ attribute: false })
  accessor session!: CopilotChatHistoryFragment | null | undefined;

  @property({ attribute: false })
  accessor createSession!: () => Promise<
    CopilotChatHistoryFragment | undefined
  >;

  @property({ attribute: false })
  accessor updateContext!: (context: Partial<ChatContextValue>) => void;

  @property({ attribute: false })
  accessor extensions!: ExtensionType[];

  @property({ attribute: false })
  accessor affineFeatureFlagService!: FeatureFlagService;

  @property({ attribute: false })
  accessor affineThemeService!: AppThemeService;

  @property({ attribute: false })
  accessor notificationService!: NotificationService;

  @property({ attribute: false })
  accessor networkSearchConfig!: AINetworkSearchConfig;

  @property({ attribute: false })
  accessor reasoningConfig!: AIReasoningConfig;

  @property({ attribute: false })
  accessor width: Signal<number | undefined> | undefined;

  @property({ attribute: false })
  accessor docDisplayService!: DocDisplayConfig;

  @property({ attribute: false })
  accessor aiToolsConfigService!: AIToolsConfigService;

  @property({ attribute: false })
  accessor aiModelService!: AIModelService;

  @property({ attribute: false })
  accessor peekViewService!: PeekViewService;

  @property({ attribute: false })
  accessor onOpenDoc!: (docId: string, sessionId?: string) => void;

  @property({ attribute: false })
  accessor workspaceDocSyncEngine: WorkspaceDocSyncEngine | null | undefined;

  @property({
    type: String,
    attribute: 'data-testid',
    reflect: true,
  })
  accessor testId = 'chat-panel-messages';

  private _visibleStartIndex = 0;

  private _lastFilteredItemsLength = 0;

  private _pendingBottomJump = false;

  private _restoringScrollAnchor = false;

  private _lastFocusedAffectedAssistantId: string | null = null;

  private _messagesResizeObserver: ResizeObserver | null = null;

  private _observedMessagesContainer: HTMLElement | null = null;

  private _resizeScrollFrame: number | null = null;

  private _filterMessages(isLoading: boolean): HistoryMessage[] {
    return this.messages.filter((item, index) => {
      const isLast = index === this.messages.length - 1;
      if (!isChatMessage(item)) return true;
      const chatMessages = this.messages.filter(isChatMessage);
      return !shouldHideChatMessage(item, chatMessages, isLast, isLoading);
    });
  }

  private _messageKey(item: HistoryMessage, index: number) {
    if (isChatMessage(item)) {
      return item.id ?? `${item.role}:${item.createdAt ?? index}:${index}`;
    }
    if (isChatAction(item)) {
      return `${item.action}:${index}`;
    }
    return index;
  }

  private _syncVisibleWindow(filteredLength: number) {
    const previousLength = this._lastFilteredItemsLength;
    const lengthChanged = previousLength !== filteredLength;

    if (!lengthChanged) return;

    const initialStart = Math.max(0, filteredLength - INITIAL_VISIBLE_MESSAGES);
    const didShrink = filteredLength < previousLength;
    const firstLoad = previousLength === 0;
    const wasAtBottom = !this.canScrollDown && !this.userScrolledUp;

    if (firstLoad || didShrink || this._visibleStartIndex > filteredLength) {
      this._visibleStartIndex = initialStart;
      this._pendingBottomJump = filteredLength > 0;
    } else if (wasAtBottom) {
      const visibleCount = Math.max(
        INITIAL_VISIBLE_MESSAGES,
        previousLength - this._visibleStartIndex
      );
      this._visibleStartIndex = Math.max(0, filteredLength - visibleCount);
      this._pendingBottomJump = true;
    }

    this._lastFilteredItemsLength = filteredLength;
  }

  private _loadPreviousWindow() {
    if (
      this._visibleStartIndex <= 0 ||
      this._restoringScrollAnchor ||
      this._pendingBottomJump ||
      this.isHistoryLoading
    ) {
      return;
    }

    const previousScrollHeight = this.scrollHeight;
    const previousScrollTop = this.scrollTop;
    this._visibleStartIndex = Math.max(
      0,
      this._visibleStartIndex - PREPEND_VISIBLE_MESSAGES
    );
    this._restoringScrollAnchor = true;
    this.requestUpdate();
    this.updateComplete
      .then(() => {
        this.scrollTop =
          this.scrollHeight - previousScrollHeight + previousScrollTop;
      })
      .finally(() => {
        this._restoringScrollAnchor = false;
      })
      .catch(console.error);
  }

  private _jumpToEnd() {
    this.canScrollDown = false;
    this.userScrolledUp = false;
    const jump = () => {
      this.scrollTop = this.scrollHeight;
    };

    jump();
    this.updateComplete
      .then(() => {
        jump();
        requestAnimationFrame(() => {
          jump();
          requestAnimationFrame(jump);
        });
      })
      .catch(console.error);
  }

  private get _isReasoningActive() {
    return isReasoningModeActive(
      this._selectedAIModel,
      this._selectedReasoningMode
    );
  }

  private get _selectedReasoningMode(): ReasoningModeId | undefined {
    return resolveReasoningMode(
      this._selectedAIModel,
      this.reasoningConfig.enabled.value,
      this.reasoningConfig.effort.value
    );
  }

  private get _selectedAIModel() {
    return resolveReasoningModel(
      this.aiModelService.models.value,
      this.aiModelService.modelId.value
    );
  }

  /**
   * Check if AI keys are configured (user, workspace, or server)
   */
  private readonly _checkAIKeys = async () => {
    try {
      const data = await fetchChatKeysCheck(this.workspaceId);
      this.hasKeys = data.hasKeys;
      this.aiStatusReason = data.reason;
      this.aiStatusIsOwner = data.isOwner;
    } catch (error) {
      console.error('[AIChatMessages] Failed to check AI keys:', error);
      // On error, assume keys are available
      this.hasKeys = true;
    }
  };

  /**
   * Render the "No Keys Configured" card for empty thread
   * Replaces the empty-chat placeholder when no keys are configured.
   */
  private _renderNoKeysCardIfNeeded() {
    return html`<ai-no-keys-card
      .title=${this.noKeysTitle}
      .description=${this.noKeysDescription}
      .buttonText=${this.noKeysButtonText}
      .reason=${this.aiStatusReason}
      .isOwner=${this.aiStatusIsOwner}
      .onOpenSettings=${this.onOpenSettings}
    ></ai-no-keys-card>`;
  }

  /**
   * Render the "No Keys Configured" card for existing thread with messages
   * Shows as additional info (like Confirmation Card pattern)
   */
  private _renderNoKeysCardForExistingThread() {
    // Only show when:
    // 1. There ARE messages (existing thread)
    // 2. Keys check confirms no keys OR there's an error (likely key-related)
    // 3. Not loading history
    // 4. No AI response yet (if AI responded, keys are working)
    const hasAIResponse = this.messages.some(
      m => 'role' in m && m.role === 'assistant'
    );
    const hasUserMessage = this.messages.some(
      m => 'role' in m && m.role === 'user'
    );
    const { status } = this.chatContextValue;
    const hasError = status === 'error';

    // Don't show if: no messages, loading, or AI already responded
    if (this.messages.length === 0 || this.isHistoryLoading || hasAIResponse) {
      return nothing;
    }

    // Show if: user sent message and (no keys confirmed OR error occurred)
    const shouldShow = hasUserMessage && (this.hasKeys === false || hasError);

    if (!shouldShow) {
      return nothing;
    }

    return html`<ai-no-keys-card
      .title=${this.noKeysTitle}
      .description=${this.noKeysDescription}
      .buttonText=${this.noKeysButtonText}
      .reason=${this.aiStatusReason}
      .isOwner=${this.aiStatusIsOwner}
      .onOpenSettings=${this.onOpenSettings}
    ></ai-no-keys-card>`;
  }

  private readonly _onScroll = () => {
    if (this._pendingBottomJump || this.isHistoryLoading) {
      this.canScrollDown = false;
      return;
    }

    const { clientHeight, scrollTop, scrollHeight } = this;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    this.canScrollDown = distanceFromBottom > 200;

    if (scrollTop < LOAD_MORE_SCROLL_THRESHOLD) {
      this._loadPreviousWindow();
    }

    // If user scrolls up during streaming, disable auto-scroll
    if (
      this.chatContextValue.status === 'transmitting' &&
      distanceFromBottom > 50
    ) {
      this.userScrolledUp = true;
    }
  };

  // Reduced debounce from 100ms to 50ms for more responsive scroll detection
  private readonly _debouncedOnScroll = debounce(this._onScroll.bind(this), 50);

  private readonly _onDownIndicatorClick = () => {
    this.canScrollDown = false;
    this.userScrolledUp = false; // Re-enable auto-scroll
    this.scrollToEnd();
  };

  protected override render() {
    const { status, error } = this.chatContextValue;
    const { isHistoryLoading } = this;

    // sl-canvas2-8.31: Determine if we're in loading state (for "AI is generating" animation)
    const isLoading =
      status === 'loading' || status === 'transmitting' || status === 'error';

    // sl-canvas2-8.31: Filter out system messages, but keep last empty message during loading
    // We need to filter with awareness of position and loading state
    const filteredItems = this._filterMessages(isLoading);
    this._syncVisibleWindow(filteredItems.length);
    const visibleStartIndex = this._visibleStartIndex;
    const visibleItems = filteredItems.slice(visibleStartIndex);

    const showDownIndicator = this.canScrollDown && filteredItems.length > 0;
    const goalActivity = this.goalPlan
      ? goalActivityText({
          tree: this.goalPlan.goalTree,
          status: this.goalPlan.status,
        })
      : null;
    const goalIsRunning =
      this.goalPlan?.status === 'queued' || this.goalPlan?.status === 'running';

    return html`
      <div
        class=${classMap({
          'chat-panel-messages-container': true,
          'independent-mode': !!this.independentMode,
        })}
        data-testid="chat-panel-messages-container"
      >
        ${filteredItems.length === 0
          ? html`<div
              class="messages-placeholder"
              data-testid="chat-panel-messages-placeholder"
            >
              ${this.hasKeys === false && !isHistoryLoading
                ? this._renderNoKeysCardIfNeeded()
                : html`
                    ${renderAIChatEmptyStateContent({
                      isLoading: isHistoryLoading,
                      panelTestIds: true,
                      showOnboarding: !this.independentMode,
                      onSelect: config => config.handler(),
                    })}
                  `}
            </div> `
          : html`
              ${visibleStartIndex > 0
                ? html`<div class="chat-history-window-sentinel"></div>`
                : nothing}
              ${repeat(
                visibleItems,
                (item, index) =>
                  this._messageKey(item, visibleStartIndex + index),
                (item, index) => {
                  const filteredIndex = visibleStartIndex + index;
                  const isLast = filteredIndex === filteredItems.length - 1;
                  if (isChatMessage(item) && item.role === 'user') {
                    return html`<chat-message-user
                      .item=${item}
                      .canvasAIChatContextMode=${this.canvasAIChatContextMode}
                    ></chat-message-user>`;
                  } else if (isChatMessage(item) && item.role === 'assistant') {
                    return html`<chat-message-assistant
                      .host=${this.host}
                      .workspaceId=${this.workspaceId}
                      .session=${this.session}
                      .item=${item}
                      .isLast=${isLast}
                      .status=${isLast ? status : 'idle'}
                      .error=${isLast ? error : null}
                      .extensions=${this.extensions}
                      .affineFeatureFlagService=${this.affineFeatureFlagService}
                      .affineThemeService=${this.affineThemeService}
                      .notificationService=${this.notificationService}
                      .retry=${() => this.retry()}
                      .width=${this.width}
                      .independentMode=${this.independentMode}
                      .docDisplayService=${this.docDisplayService}
                      .codeSelections=${this.chatContextValue.codeSelections ||
                      []}
                      .peekViewService=${this.peekViewService}
                      .onOpenDoc=${this.onOpenDoc}
                      .workspaceDocSyncEngine=${this.workspaceDocSyncEngine}
                      .onOpenSettings=${this.aiStatusIsOwner
                        ? this.onOpenSettings
                        : undefined}
                    ></chat-message-assistant>`;
                  } else if (isChatAction(item) && this.host) {
                    return html`<chat-message-action
                      .host=${this.host}
                      .item=${item}
                    ></chat-message-action>`;
                  }
                  return nothing;
                }
              )}
            `}
        ${goalActivity
          ? html`<div
              class="goal-activity"
              data-testid="chat-goal-activity"
              role="status"
              aria-live="polite"
            >
              ${renderAssistantIdentity(
                goalIsRunning ? 'transmitting' : 'idle'
              )}
              <div class="goal-activity-status">
                ${goalIsRunning
                  ? html`<span class="goal-activity-dots" aria-hidden="true"
                      ><i></i><i></i><i></i
                    ></span>`
                  : nothing}
                <span>${goalActivity}</span>
              </div>
            </div>`
          : nothing}
        ${this._renderNoKeysCardForExistingThread()}
      </div>
      ${showDownIndicator && filteredItems.length > 0
        ? html`<div
            data-testid="chat-panel-scroll-down-indicator"
            class="down-indicator"
            @click=${this._onDownIndicatorClick}
          >
            ${ArrowDownIcon()}
          </div>`
        : nothing}
    `;
  }

  override connectedCallback() {
    super.connectedCallback();
    const { disposables } = this;

    // Check if AI keys are configured
    this._checkAIKeys().catch(console.error);

    Promise.resolve(AIProvider.userInfo)
      .then(res => {
        this.avatarUrl = res?.avatarUrl ?? '';
      })
      .catch(console.error);

    disposables.add(
      AIProvider.slots.userInfo.subscribe(userInfo => {
        const { status, error } = this.chatContextValue;
        this.avatarUrl = userInfo?.avatarUrl ?? '';
        if (
          status === 'error' &&
          error instanceof UnauthorizedError &&
          userInfo
        ) {
          this.updateContext({ status: 'idle', error: null });
        }
      })
    );

    const selection$ = this.host?.selection.slots.changed;
    if (selection$) {
      disposables.add(
        selection$.subscribe(() => {
          this._selectionValue = this.host?.selection.value ?? [];
        })
      );
    }

    const docModeService = this.host?.std.get(DocModeProvider);
    if (docModeService && this.docId) {
      disposables.add(
        docModeService.onPrimaryModeChange(
          () => this.requestUpdate(),
          this.docId
        )
      );
    }

    // Add scroll event listener to the host element
    this.addEventListener('scroll', this._debouncedOnScroll);
    disposables.add(() => {
      this.removeEventListener('scroll', this._debouncedOnScroll);
    });

    disposables.add(() => {
      this._messagesResizeObserver?.disconnect();
      this._messagesResizeObserver = null;
      this._observedMessagesContainer = null;
      if (this._resizeScrollFrame !== null) {
        cancelAnimationFrame(this._resizeScrollFrame);
        this._resizeScrollFrame = null;
      }
    });

    // sl-canvas2-8.31: Listen for confirmation completed events to auto-continue
    disposables.add(
      confirmationCompletedEventBus.subscribe(this._handleConfirmationCompleted)
    );
  }

  /**
   * sl-canvas2-8.31 Phase 6: Handle confirmation completed events
   *
   * KEIN neuer Stream! Das Tool-Result ist bereits als Message gespeichert.
   * Wir müssen nur die UI aktualisieren und eine lokale Bestätigung anzeigen.
   *
   * Der /respond Endpoint speichert:
   * 1. tool-approval-response (User's decision)
   * 2. tool-result (Execution result)
   *
   * Diese werden bei der nächsten History-Aktualisierung angezeigt.
   */
  private readonly _handleConfirmationCompleted = (
    detail: ConfirmationCompletedEventDetail
  ) => {
    const currentSessionId = this.session?.sessionId;

    // Only handle if this is for our session
    if (currentSessionId !== detail.sessionId) return;

    // sl-z4tb: Update the LAST assistant message's streamObjects
    // to transition tool-call → tool-result (fixes stuck loading card + no second bubble)
    const messages = [...this.chatContextValue.messages];
    const lastAssistant = [...messages]
      .reverse()
      .find(m => isChatMessage(m) && m.role === 'assistant');

    if (lastAssistant && isChatMessage(lastAssistant)) {
      const idx = messages.indexOf(lastAssistant);
      const streamObjects = [...(lastAssistant.streamObjects ?? [])];

      // Find the tool-call that was awaiting confirmation and replace with tool-result
      const toolCallIdx = streamObjects.findIndex(
        so => so.type === 'tool-call'
      );
      if (toolCallIdx !== -1) {
        const tc = streamObjects[toolCallIdx] as {
          type: 'tool-call';
          toolCallId: string;
          toolName: string;
          displayName?: string;
          args: Record<string, unknown>;
        };
        streamObjects[toolCallIdx] = {
          type: 'tool-result' as const,
          toolCallId: tc.toolCallId,
          toolName: tc.toolName,
          // sl-z4tb: Preserve displayName from tool-call → tool-result transition
          ...(tc.displayName ? { displayName: tc.displayName } : {}),
          args: tc.args,
          result: detail.success
            ? {
                success: true,
                data: detail.toolResult?.data ?? detail.toolResult?.result,
              }
            : { success: false, error: detail.toolResult?.error },
        };
      }

      // Also remove any tool-execution-started banners
      const tesIdx = streamObjects.findIndex(
        so => so.type === 'tool-execution-started'
      );
      if (tesIdx !== -1) {
        streamObjects.splice(tesIdx, 1);
      }

      messages[idx] = {
        ...lastAssistant,
        streamObjects,
      };
    }

    this.updateContext({ messages, status: 'success' });
    this.scrollToEnd();
  };

  protected override updated(_changedProperties: PropertyValues) {
    this._syncMessagesResizeObserver();

    // If we got an AI response, keys must be working - update state
    if (_changedProperties.has('messages') && this.hasKeys === false) {
      const hasAIResponse = this.messages.some(
        m => 'role' in m && m.role === 'assistant'
      );
      if (hasAIResponse) {
        this.hasKeys = true;
      }
    }

    if (_changedProperties.has('isHistoryLoading')) {
      const wasLoading = _changedProperties.get('isHistoryLoading') as boolean;
      // Jump to end (no animation) when history finishes loading
      if (wasLoading && !this.isHistoryLoading && this.messages.length > 0) {
        this._pendingBottomJump = true;
      }
      this.canScrollDown = false;
      this.userScrolledUp = false;
    }

    if (_changedProperties.has('chatContextValue')) {
      const prevContext = _changedProperties.get('chatContextValue') as
        | ChatContextValue
        | undefined;
      const { status } = this.chatContextValue;

      // Reset userScrolledUp and scroll to end when user sends a message
      if (
        status === 'loading' &&
        prevContext?.status !== 'loading' &&
        prevContext?.status !== 'transmitting'
      ) {
        this.userScrolledUp = false;
        // Scroll to bottom immediately when user sends message
        this.scrollToEnd();
      }

      // Auto-scroll during streaming if user hasn't scrolled up
      if (status === 'transmitting') {
        this._onScroll();
        if (!this.userScrolledUp) {
          this.scrollToEnd();
        }
      }

      // Auto-scroll when response completes (success)
      if (
        status === 'success' &&
        prevContext?.status === 'transmitting' &&
        !this.userScrolledUp
      ) {
        this.scrollToEnd();
      }

      if (status === 'success' && prevContext?.status === 'transmitting') {
        this._focusLatestAffectedCanvasObjects();
      }
    }

    if (this._pendingBottomJump) {
      this._pendingBottomJump = false;
      this._jumpToEnd();
    }
  }

  private _focusLatestAffectedCanvasObjects() {
    const latestAssistant = [...this.messages]
      .reverse()
      .find(
        (message): message is ChatMessage =>
          isChatMessage(message) && message.role === 'assistant'
      );
    if (!latestAssistant?.id) return;
    if (latestAssistant.id === this._lastFocusedAffectedAssistantId) return;

    const refs = this._collectCanvasAffectedRefs(latestAssistant.streamObjects);
    if (!refs.length) return;

    const docId = refs[0].docId;
    if (!docId) return;
    const sameDocRefs = refs.filter(ref => ref.docId === docId);
    const elementIds = [
      ...new Set(
        sameDocRefs.map(ref => ref.elementId).filter((id): id is string => !!id)
      ),
    ];
    const blockIds = [
      ...new Set(
        sameDocRefs.map(ref => ref.blockId).filter((id): id is string => !!id)
      ),
    ];
    if (!elementIds.length && !blockIds.length) return;

    this._lastFocusedAffectedAssistantId = latestAssistant.id;
    this.dispatchEvent(
      new CustomEvent('navigate-to-source', {
        bubbles: true,
        composed: true,
        detail: {
          kind: 'canvas-affected-ref',
          docId,
          blockIds,
          elementIds,
          requestId: `ai-affected-${latestAssistant.id}`,
        },
      })
    );
  }

  private _collectCanvasAffectedRefs(streamObjects?: StreamObject[] | null) {
    if (!streamObjects?.length) return [];
    return streamObjects
      .filter(object => object.type === 'tool-result')
      .flatMap(object => object.affectedRefs ?? [])
      .filter(
        ref =>
          (ref.kind === 'canvas_element' || ref.kind === 'block') && ref.docId
      );
  }

  private _shouldStickToBottomAfterContentResize() {
    return (
      this.messages.length > 0 &&
      !this.canScrollDown &&
      !this.userScrolledUp &&
      !this._restoringScrollAnchor &&
      !this.isHistoryLoading
    );
  }

  private readonly _scheduleBottomStickAfterContentResize = () => {
    if (!this._shouldStickToBottomAfterContentResize()) return;
    if (this._resizeScrollFrame !== null) return;

    this._resizeScrollFrame = requestAnimationFrame(() => {
      this._resizeScrollFrame = null;
      if (this._shouldStickToBottomAfterContentResize()) {
        this.scrollToEnd();
      }
    });
  };

  private _syncMessagesResizeObserver() {
    if (typeof ResizeObserver === 'undefined') return;

    const container = this.querySelector<HTMLElement>(
      '[data-testid="chat-panel-messages-container"]'
    );
    if (!container || container === this._observedMessagesContainer) return;

    this._messagesResizeObserver ??= new ResizeObserver(
      this._scheduleBottomStickAfterContentResize
    );
    this._messagesResizeObserver.disconnect();
    this._messagesResizeObserver.observe(container);
    this._observedMessagesContainer = container;
  }

  scrollToEnd() {
    requestAnimationFrame(() => {
      const targetScroll = this.scrollHeight;
      this.scrollTo({
        top: targetScroll,
        behavior: 'auto',
      });
      // Double-check scroll position after animation completes
      // This handles cases where content height changed during scroll
      setTimeout(() => {
        if (this.scrollHeight > targetScroll) {
          this.scrollTo({
            top: this.scrollHeight,
            behavior: 'auto',
          });
        }
      }, 350);
    });
  }

  scrollToPos(top: number) {
    requestAnimationFrame(() => {
      this.scrollTo({ top });
    });
  }

  private readonly _syncMissingMessageIds = async (
    sessionId: string
  ): Promise<HistoryMessage[]> => {
    const { messages } = this.chatContextValue;
    if (!AIProvider.histories) return messages;

    const historyIds = await AIProvider.histories.ids(
      this.workspaceId,
      this.docId,
      { sessionId, withMessages: true }
    );
    const persistedMessages = historyIds?.[0]?.messages ?? [];
    const nextMessages = syncMissingMessageIds(messages, persistedMessages);
    if (!nextMessages) return messages;

    this.updateContext({ messages: nextMessages });
    return nextMessages;
  };

  private _showOfflineAIToast() {
    this.notificationService?.notify({
      title: I18n.t('com.streamlain.ai.offline.unavailable'),
      message: I18n.t('com.streamlain.ai.offline.detail'),
      accent: 'warning',
    });
  }

  retry = async () => {
    try {
      if (!getAIChatOnlineStatus()) {
        this._showOfflineAIToast();
        return;
      }

      const sessionId = (await this.createSession())?.sessionId;
      if (!sessionId) return;
      if (!AIProvider.actions.chat) return;

      const syncedMessages = await this._syncMissingMessageIds(sessionId);

      const abortController = new AbortController();
      const messages = [...syncedMessages];
      // user-message that triggered the failed assistant response — its id is
      // the triggerId backend uses to bucket tool-loop sub-calls. Retry without
      // it would force trackStreamUsage(lifecycle='partial') to throw.
      const lastUserMessage = [...messages]
        .reverse()
        .find(m => isChatMessage(m) && m.role === 'user');
      if (!lastUserMessage || !isChatMessage(lastUserMessage)) return;
      if (!lastUserMessage.id) {
        throw new Error('Retry is unavailable until the user message is saved');
      }
      const last = messages[messages.length - 1];
      if ('content' in last) {
        last.content = '';
        last.streamObjects = [];
        last.createdAt = new Date().toISOString();
      }
      this.updateContext({
        messages,
        status: 'loading',
        error: null,
        abortController,
      });

      const retrievalPlan = resolveCopilotRetrievalPlan(
        lastUserMessage.content,
        this.aiToolsConfigService.config.value,
        !!this.networkSearchConfig.visible.value
      );

      const stream = await AIProvider.actions.chat({
        sessionId,
        retry: true,
        messageId: lastUserMessage.id,
        modelId: this.aiModelService.modelId.value,
        docId: this.docId,
        workspaceId: this.workspaceId,
        stream: true,
        signal: abortController.signal,
        where: 'chat-panel',
        control: 'chat-send',
        isRootSession: true,
        reasoning: this._isReasoningActive,
        reasoningEffort: this._selectedReasoningMode,
        webSearch: retrievalPlan.webSearch,
        toolsConfig: retrievalPlan.toolsConfig,
      });

      for await (const text of stream) {
        const messages = this.chatContextValue.messages.slice(0);
        const last = messages.at(-1);
        if (last && isChatMessage(last)) {
          try {
            const parsed = StreamObjectSchema.parse(JSON.parse(text));
            const streamObjects = mergeStreamObjects([
              ...(last.streamObjects ?? []),
              parsed,
            ]);
            messages[messages.length - 1] = {
              ...last,
              streamObjects,
            };
          } catch {
            if (last.streamObjects?.length) {
              // When streamObjects exist (e.g. reasoning blocks), raw text
              // must join them as text-delta — otherwise renderContent()
              // only renders streamObjects and the text content is lost.
              const streamObjects = mergeStreamObjects([
                ...(last.streamObjects ?? []),
                { type: 'text-delta' as const, textDelta: text },
              ]);
              messages[messages.length - 1] = {
                ...last,
                streamObjects,
              };
            } else {
              messages[messages.length - 1] = {
                ...last,
                content: last.content + text,
              };
            }
          }
          this.updateContext({ messages, status: 'transmitting' });
        }
      }

      this.updateContext({ status: 'success' });
    } catch (error) {
      this.updateContext({ status: 'error', error: error as AIError });
    } finally {
      this.updateContext({ abortController: null });
    }
  };
}
